import React from "react";
import { Formik, FieldArray, useFormikContext } from "formik";
import { useEffect } from "react";
import apiRequest from "../../../utils/admin/apiRequest";
import InputField from "../../ui/form/InputField";
import Button from "../../ui/utils/Button";
import SelectField from "../../ui/form/SelectField";
import usePost from "../../../hooks/api/usePost";
import Alert from "../../ui/utils/Alert";
import useFetch from "../../../hooks/api/useFetch";
import { adminValidationSchema } from "../../../validation/admin/authValidation";
import { CgAdd } from "react-icons/cg";
import { Trash } from "lucide-react";
import { generateUserId } from "../../../utils/helper";
import AsyncSearchSelect from "../../ui/form/AsyncSearchSelect";


const LocationWatcher = ({
  fetchStates,
  fetchCities,
  setBlankResponse,
  setCityBlankResponse,
}) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.countryId) {
      fetchStates({ countryId: values.countryId });

      if (!values.stateId) {
        setFieldValue("stateId", "");
      }
    } else {
      setBlankResponse();
    }
  }, [values.countryId]);

  useEffect(() => {
    if (values.stateId) {
      fetchCities({ stateId: values.stateId });

      if (!values.cityId) {
        setFieldValue("cityId", "");
      }
    } else {
      setCityBlankResponse();
    }
  }, [values.stateId]);

  return null;
};

/* ---------- UserId Generator ---------- */
const UserIdWatcher = ({ generateUserId }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.name && values.mobile) {
      setFieldValue(
        "userId",
        generateUserId(values.name, values.mobile)
      );
    }
  }, [values.name, values.mobile]);

  return null;
};

/* ---------- Main Component ---------- */
const CreateAdmin = ({ closeModal, callback }) => {
  const { error, isLoading, post, serverErrors } = usePost(
    apiRequest.createAdmin
  );

  const initialValues = {
    name: "",
    mobile: "",
    email: "",
    password: "",
    contactPerson: "",
    userId: "",
    countryId: "6937b28dda25e4d40acc2983",
    stateId: "6937b35cda25e4d40acc4ce4",
    cityId: "",
    corporateCodeId: [""],
  };

  const { data: countryData } = useFetch({
    apiMethod: apiRequest.getCountry,
    body: { paginate: false },
  });

  const { data: stateData, refetch: fetchStates, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getStateByCountryId,
    autoFetch: false,
  });

  const {
    data: cityData,
    refetch: fetchCities,
    setBlankResponse: setCityBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCityByState,
    autoFetch: false,
  });



  const fetchCorporateCodes = async (search) => {
    const res = await apiRequest.getCorporateCodeForSelect({
      body: { filters: { name: { $regex: search, $options: "i" }, }, paginate: false, },
    });
    return (res?.data?.data?.corporatecodes?.map((v) => ({
      label: `${v.name} (${v.code})`,
      value: v.id,
    })) || []);
  };


  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Create Admin Head</h2>
      <div className="border-b" />

      <Formik
        initialValues={initialValues}
        validationSchema={adminValidationSchema}
        onSubmit={async (values, { resetForm }) => {
          const response = await post({ data: { body: values } });
          if (response?.response) {
            callback?.();
            resetForm();
            closeModal();
          }
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, touched, errors }) => (
          <>
            <LocationWatcher
              fetchStates={fetchStates}
              fetchCities={fetchCities}
              setBlankResponse={setBlankResponse}
              setCityBlankResponse={setCityBlankResponse}
            />
            <UserIdWatcher generateUserId={generateUserId} />

            <form
              onSubmit={handleSubmit}
              className="text-sm max-h-[80vh] overflow-auto"
            >
              {error && <Alert message={error} type="error" />}

              <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2">
                <InputField
                  name="name"
                  label="Admin Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && errors.name}
                />

                <InputField
                  name="mobile"
                  label="Mobile"
                  value={values.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.mobile && errors.mobile}
                />

                <InputField
                  name="email"
                  label="Email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && errors.email}
                />

                <SelectField
                  name="countryId"
                  label="Country"
                  value={values.countryId}
                  onChange={handleChange}
                  options={countryData?.countries?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}

                />

                <SelectField
                  name="stateId"
                  label="State"
                  value={values.stateId}
                  onChange={handleChange}
                  options={stateData?.states?.map((s) => ({
                    value: s._id,
                    label: s.name,
                  }))}
                />

                <SelectField
                  name="cityId"
                  label="City"
                  value={values.cityId}
                  onChange={handleChange}
                  options={cityData?.cities?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                />

                <InputField
                  name="contactPerson"
                  label="Contact Person"
                  value={values.contactPerson}
                  onChange={handleChange}
                  error={touched.contactPerson && errors.contactPerson}

                />

                <InputField
                  name="userId"
                  label="User ID"
                  value={values.userId}
                  onChange={handleChange}
                  error={touched.userId && errors.userId}

                />

                <InputField
                  type="password"
                  name="password"
                  label="Password"
                  value={values.password}
                  onChange={handleChange}
                  error={touched.password && errors.password}

                />

                {/* ---------- Corporate Codes ---------- */}
                <div className="col-span-full">
                  <FieldArray name="corporateCodeId">
                    {({ push, remove }) => (
                      <>
                        {values.corporateCodeId.map((_, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 mb-2"
                          >
                            <AsyncSearchSelect
                              name={`corporateCodeId.${index}`}
                              placeholder="Select Corporate Code"
                              value={values.corporateCodeId[index]}
                              onChange={handleChange}
                              fetchOptions={fetchCorporateCodes}
                              error={touched.corporateCodeId && errors.corporateCodeId}
                            />
                          
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="text-red-500"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={() => push("")}
                          className="mt-2 px-2 py-1 bg-blue-500 text-white text-xs rounded flex items-center gap-2"
                        >
                          <CgAdd /> Add Corporate Code
                        </button>
                      </>
                    )}
                  </FieldArray>
                </div>
              </div>

              <div className="border-b" />

              <div className="flex justify-end gap-2 p-4">
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={isLoading}>
                  Create
                </Button>
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateAdmin;
